"use client"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, CheckCircle, Send, Clock } from "lucide-react"
import TokenChecker from "../TokenChecker"

const formatDateTime = (value) => {
  if (!value) return null
  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

export default function FlowSyncHistory() {
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  const URL_PreFix = "/api/flowsync/"

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await TokenChecker(`${URL_PreFix}history/`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(payload.error || "Failed to load FlowSync history.")
        }
        setEvents(payload.events || [])
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load FlowSync history.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchHistory()
  }, [URL_PreFix])

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
              <ChevronLeft className="inline-block w-6 h-6 mr-6 cursor-pointer text-[#10A7F3]" onClick={() => navigate("/flowsync")} />
              FlowSync History
            </span>
            <p className="text-sm ml-8 text-gray-500">
              Tasks added and responses sent from FlowSync
            </p>
          </div>
          <button
            onClick={() => navigate("/flowsync")}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Clock className="w-4 h-4" />
            Back to FlowSync
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8 space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
            Loading history…
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No history yet</p>
            <p className="text-sm text-gray-400 mt-2">
              Added tasks and sent drafts will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => {
              const isTask = event.type === "task_added"
              return (
                <article
                  key={`${event.type}-${event.id}`}
                  className="bg-white shadow-sm rounded-md border border-gray-200 p-6"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 rounded-full p-2 ${isTask ? "bg-green-50 text-green-600" : "bg-blue-50 text-blue-600"}`}>
                        {isTask ? <CheckCircle className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {isTask ? "Task Added" : "Draft Sent"}
                        </p>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {isTask ? event.title : event.subject || "Untitled"}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {isTask
                            ? `From ${event.source_email_sender || "Unknown sender"}`
                            : `To ${event.recipient || "Unknown recipient"}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-gray-400 space-y-1">
                      <p>Updated {formatDateTime(event.updated_at) || "recently"}</p>
                      <p>Created {formatDateTime(event.created_at) || "recently"}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-blue-600">
                      Original subject: {event.source_email_subject || event.original_subject || "Unknown subject"}
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-blue-600">
                      From: {event.source_email_sender || event.original_sender || "Unknown sender"}
                    </span>
                  </div>
                  {isTask && event.description && (
                    <div className="mt-4 text-sm text-gray-600 bg-gray-50 rounded-md p-4 whitespace-pre-line">
                      {event.description}
                    </div>
                  )}
                </article>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}
